import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Field } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useI18n, ROLES } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

function Mfa() {
  const { tx } = useI18n();
  const { user, reload, mfaEnforced } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const start = async () => { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); };
  const enable = async () => { try { await api.post("/auth/mfa/enable", { code }); toast.success(tx("MFA activée", "MFA enabled")); setSetup(null); setCode(""); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const disable = async () => { try { await api.post("/auth/mfa/disable", { code, password: pw }); toast.success(tx("MFA désactivée", "MFA disabled")); setCode(""); setPw(""); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="card-surface p-4 space-y-3" data-testid="mfa-section">
      <div className="font-display font-semibold text-[#153A63]">{tx("Authentification à deux facteurs (TOTP)", "Two-factor authentication (TOTP)")}</div>
      {mfaEnforced && !user.mfa_enabled && <div className="text-sm text-rose-700" data-testid="mfa-required-notice">{tx("La MFA est obligatoire avant d'accéder aux missions.", "MFA is required before accessing missions.")}</div>}
      <div className="text-sm" data-testid="mfa-status">{user.mfa_enabled ? tx("✓ Activée", "✓ Enabled") : tx("Non activée", "Not enabled")}</div>
      {!user.mfa_enabled && !setup && <Button data-testid="mfa-setup-btn" onClick={start} className="bg-[#153A63]">{tx("Configurer", "Set up")}</Button>}
      {setup && (
        <div className="flex flex-wrap gap-4 items-start">
          <img src={setup.qr} alt="QR" className="h-40 w-40 border rounded" data-testid="mfa-qr" />
          <div className="space-y-2 text-sm">
            <p>{tx("Scannez avec votre application (Google Authenticator, Microsoft Authenticator…) ou saisissez la clé :", "Scan with your app (Google Authenticator, Microsoft Authenticator…) or enter the key:")}</p>
            <code className="block text-xs bg-slate-100 p-2 rounded break-all" data-testid="mfa-secret">{setup.secret}</code>
            <div className="flex gap-2"><Input data-testid="mfa-code-input" className="w-32 font-mono" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" /><Button data-testid="mfa-enable-btn" onClick={enable} className="bg-[#153A63]">{tx("Activer", "Enable")}</Button></div>
          </div>
        </div>
      )}
      {user.mfa_enabled && (
        <div className="flex flex-wrap gap-2 items-end">
          <Field label={tx("Mot de passe", "Password")}><Input type="password" data-testid="mfa-disable-password" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
          <Field label={tx("Code", "Code")}><Input data-testid="mfa-disable-code" className="w-28 font-mono" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
          <Button variant="outline" data-testid="mfa-disable-btn" onClick={disable}>{tx("Désactiver", "Disable")}</Button>
        </div>
      )}
    </section>
  );
}

function Sessions() {
  const { tx } = useI18n();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/auth/sessions").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="card-surface overflow-x-auto">
      <div className="px-4 py-3 border-b font-display font-semibold text-[#153A63]">{tx("Mes sessions actives", "My active sessions")}</div>
      <table className="tbl text-xs" data-testid="my-sessions-table"><tbody>{rows.map((s) => (
        <tr key={s.sid}><td>{fmtDate(s.created_at)}</td><td>{s.ip}</td><td className="max-w-xs truncate">{s.user_agent}</td><td>{s.current ? <b>{tx("Session actuelle", "Current")}</b> : <Button size="sm" variant="outline" data-testid={`revoke-my-session-${s.sid}`} onClick={async () => { await api.post(`/auth/sessions/${s.sid}/revoke`); load(); }}>{tx("Révoquer", "Revoke")}</Button>}</td></tr>))}
      </tbody></table>
    </section>
  );
}

export default function Account() {
  const { tx, lbl } = useI18n();
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const change = async () => { try { await api.post("/auth/change-password", pw); toast.success(tx("Mot de passe modifié ; autres sessions fermées", "Password changed; other sessions closed")); setPw({ current_password: "", new_password: "" }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={tx("Compte", "Account")} title={user.name} subtitle={`${user.email} · ${lbl(ROLES, user.platform_role)}`} />
      <Mfa />
      <section className="card-surface p-4 flex flex-wrap gap-3 items-end">
        <div className="w-full font-display font-semibold text-[#153A63]">{tx("Changer le mot de passe", "Change password")}</div>
        <Field label={tx("Actuel", "Current")}><Input type="password" data-testid="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></Field>
        <Field label={tx("Nouveau (10+ car., lettres et chiffres)", "New (10+ chars, letters & digits)")}><Input type="password" data-testid="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></Field>
        <Button data-testid="change-password-btn" onClick={change} className="bg-[#153A63]">{tx("Modifier", "Change")}</Button>
      </section>
      <Sessions />
    </div>
  );
}
